import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { distinctUntilChanged, startWith, switchMap, tap } from 'rxjs/operators';
import { Unsubscribable } from 'rxjs';
import * as _ from 'lodash';
import { FileUploadControl } from '@iplab/ngx-file-upload';
import { DmsService } from 'app/shared/common/utils/DmsService';
import { UtilComponent } from 'app/shared/common/utils/UtilComponent';
import { FileUploadUtils } from './FileUploadUtils';
import { Events } from 'app/shared/common/utils/Events';

@Component({
    selector: 'multi-files-upload-large-widget',
    template: `
        <div fxLayout="row"
             fxLayoutAlign="start center"
             fxLayoutGap="10px"
             style="width: '100%'">
            <file-upload
                    [formControl]="uploadFormControl"
                    [style.width]="'100%'"
                         [control]="uploadControl"
                         [accept]="options?.accept"
                         [maxsize]="options?.maxsize"
                         [minsize]="1"
                         [fileslimit]="options?.fileslimit"
                         [animation]="false"
            >
            <ng-template let-i="index" let-file="file" #item>
            <div class="file-container" fxLayout="row" fxLayoutGap="5px"
                 fxLayoutAlign="start center">
                <div class="file-info" fxFlex fxLayout="row" fxLayoutGap="5px"
                     fxLayoutAlign="start center">
                    <file-upload-type-icon [file]="file"></file-upload-type-icon>
                    <span>
                        <span class="file-name">{{ file.name }} <span class="file-size">{{ renderSize(file.size) }}</span></span>
                    </span>
                </div>
                <div class="file-buttons" fxLayout="row"
                     fxLayoutAlign="space-between center" fxLayoutGap="5px">
                    <div *ngIf="file.uploading" class="uploading"
                         fxLayout="row" fxLayoutAlign="center center">
                        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                    </div>

                    <div *ngIf="file.downloading" class="downloading"
                         fxLayout="row" fxLayoutAlign="center center">
                        <mat-progress-bar mode="buffer"></mat-progress-bar>
                    </div>
                    <div *ngIf="!file.downloading && !file.deleting && file.identifier">
                        <button mat-icon-button class="btn-icon" (click)="downloadFile(file)">
                            <mat-icon>get_app</mat-icon>
                        </button>
                    </div>

                    <div *ngIf="!this.controlDisabled && file.deleting" class="deleting"
                         fxLayout="row" fxLayoutAlign="center center">
                        <mat-progress-bar mode="indeterminate" color="warn"></mat-progress-bar>
                    </div>
                    <div *ngIf="!this.controlDisabled && !file.downloading && !file.deleting && file.identifier">
                        <button mat-icon-button class="btn-icon" (click)="removeFile(file)">
                            <mat-icon>delete</mat-icon>
                        </button>
                    </div>
                </div>
            </div>
        </ng-template>
            </file-upload>
        </div>

        <mat-hint *ngIf="options?.description" class="hint"
            align="end" [innerHTML]="options?.description | transloco">
        </mat-hint>
    `,
    styles: [`
        file-upload .file-container {
            min-height: 40px;
        }

        file-upload-type-icon {
            width: 32px;
            height: 36px;
        }

        file-upload .file-buttons .btn-icon {
            width: 36px;
            height: 36px;
        }

        file-upload .file-buttons mat-icon {
            width: 28px;
            height: 28px;
        }

        file-upload .file-buttons div.uploading {
            width: 72px;
            height: 36px;
        }

        file-upload .file-buttons div.downloading {
            width: 72px;
            height: 36px;
        }

        file-upload .file-buttons div.deleting {
            width: 72px;
            height: 36px;
        }

        file-upload .file-buttons mat-icon:hover {
            color: #80a9d2;
        }

        mat-hint.hint {
            font-size: 12px;
            color: #E03137;
        }
    `],
})
export class MultiFilesUploadLargeComponent implements OnInit, OnDestroy {
    formControl: AbstractControl;
    controlName: string;
    controlValue: string;
    controlDisabled = false;
    boundControl = false;
    options: any;

    @Input() layoutNode: any;

    uploadFormControl: FormControl;
    uploadControl = new FileUploadControl();
    private _unsubscribableArray: Array<Unsubscribable> = [];

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _dmsService: DmsService,
        private _utilComponent: UtilComponent
    ) {
    }

    ngOnInit(): void {
        this.options = this.layoutNode.options || {};
        // options.accept = 'image/*,audio/*,.mp4' for all images, audios and mp4.
        this.options.accept = this.options.accept || ''; // default all files
        this.controlDisabled = this.options.controlDisabled;

        this.uploadFormControl = new FormControl([]);
        this.formControl = new FormControl([]);
        if (this.controlDisabled) {
            this.uploadFormControl.disable();
        }

        this._initSubscribe();
    }

    ngOnDestroy(): void {
        this._unsubscribableArray.forEach(s => {
            if (s && s.unsubscribe) {
                s.unsubscribe();
            }
        });
    }

    public getUploadFormControl(): any {
        return this.uploadFormControl;
    }

    public downloadFile(file: any): void {
        if (file) {
            file.downloading = true;
            this._changeDetectorRef.detectChanges();
            setTimeout(() => {
                const { identifier, name } = file;
                this._dmsService.getMultiPartContent(identifier).then(blob => {
                    if (!blob) {
                        this._utilComponent.showTranslateSnackbar('SYSTEM_ERROR', 'error');
                    } else {
                        this._dmsService.saveAsBlob(blob, name);
                    }
                    file.downloading = false;
                    this._changeDetectorRef.detectChanges();
                }).catch(error => {
                    console.error(`Error when download file: ${file.identifier}`, error);
                    this._utilComponent.showTranslateSnackbar('SYSTEM_ERROR', 'error');
                    file.downloading = false;
                    this._changeDetectorRef.detectChanges();
                });
            }, 100);
        }
    }

    public removeFile(file: any): void {
        if (file) {
            if (file.identifier) {
                file.deleting = true;
                this._changeDetectorRef.detectChanges();

                // call service to delete file
                setTimeout(() => {
                    this._dmsService.delete(file.identifier).then(resData => {
                        if (resData && resData.errorcode) {
                            if (resData.message) {
                                this._utilComponent.openSnackBar(resData.message, 'error');
                            } else {
                                this._utilComponent.showTranslateSnackbar('SYSTEM_ERROR', 'error');
                            }
                        }
                        this.uploadControl.removeFile(file);
                        this._changeDetectorRef.detectChanges();
                    }).catch(error => {
                        console.error(`Error when delete file: ${file.identifier}`, error);
                        this._utilComponent.showTranslateSnackbar('SYSTEM_ERROR', 'error');
                        this.uploadControl.removeFile(file);
                        this._changeDetectorRef.detectChanges();
                    });
                }, 100);
            } else {
                this.uploadControl.removeFile(file);
            }
        }
    }

    public uploadFiles(files): Promise<any> {
        if (_.isEmpty(files)) {
            return Promise.resolve([]);
        }

        return Promise.all(
            _.map(files, async (file) => {
                if (file.identifier) {
                    return file;
                }
                if (this.options.maxsize
                    && file.size > (this.options.maxsize * 1024)) {
                    file.uploading = false;
                    return undefined;
                }
                if (this.options.accept) {
                    let acceptArr = this.options.accept.split(',');
                    let isAccepted = false;
                    for (let obj of acceptArr) {
                        if (file.name.endsWith(obj)) {
                            isAccepted = true;
                            break;
                        }
                    }
                    if (!isAccepted) {
                        file.uploading = false;
                        return undefined;
                    }
                }

                try {
                    const formData: FormData = new FormData();
                    formData.append('file', file, file.name);
                    formData.append('cmd', JSON.stringify({
                        'name': file.name
                    }));
                    let resData = null;
                    const uploadSize = 20971520; // Bytes = 20MB
                    if (file.size < uploadSize) {
                        resData = await this._dmsService.postMultiPartContent(formData);
                    } else {
                        // resData = await this._dmsService.postMultiPartContentLargeFile(formData, file.name);
                        Events.publish('event:bulkUploadLargeFile', file);
                        return undefined;
                    }
                    if (resData && resData.errorcode) {
                        return undefined;
                    }

                    file.uploading = false;
                    file.identifier = resData;
                    if (file.size === 0) {
                        this._utilComponent.openSnackBar('Upload file failed!', 'error');
                        return undefined;
                    }
                    return file;
                } catch (e) {
                    if (e && e.name && e.name === 'TimeoutError') {
                        this._utilComponent.showTranslateSnackbar('GENERAL.SERVICE_TIMEOUT', 'error');
                        return undefined;
                    }
                    console.error(`Error when read or upload file: ${file.name}`, e);
                    this._utilComponent.showTranslateSnackbar('SYSTEM_ERROR', 'error');
                    return undefined;
                }
            })
        );
    }

    public renderSize(size: number): string {
        if (size > 0) {
            return `(${FileUploadUtils.calculateSize(size)})`;
        }
        return '';
    }

    private _initSubscribe(): void {
        this._unsubscribableArray.push(
            // formControl of json form (model) -> uploadFormControl (UI)
            this.formControl.valueChanges.pipe(
                startWith(this.controlValue),
                switchMap(strValue => {
                    let array = [];
                    try {
                        if (_.isString(strValue)) {
                            array = JSON.parse(strValue);
                        }

                        array = _.filter(array, f => !_.isEmpty(f.identifier));
                    } catch (e) {
                        console.error('Can not parse json', e);
                    }

                    if (_.isEmpty(array) || _.every(array, f => !_.isEmpty(f.name))) {
                        return Promise.resolve(array);
                    }

                    const refCodes = _.map(array, f => f.identifier);
                    return this._dmsService.getResources(refCodes).then(resources => {
                        _.forEach(array, f => {
                            const resource = _.find(resources, r => r.refCode === f.identifier);
                            if (resource) {
                                f.name = resource.fileName;
                                if (resource.size > 0) {
                                    f.size = resource.size;
                                }
                                f.type = resource.mimeType;
                            }
                        });
                        return array;
                    }).catch((error) => {
                        console.error('Can\'t not get resources', error);
                        return array;
                    });
                }),
                distinctUntilChanged(FileUploadUtils.isEqualFiles)
            ).subscribe(files => {
                // avoid update 2 times
                if (!FileUploadUtils.isEqualFiles(this.uploadFormControl.value, files)) {
                    this.uploadFormControl.setValue(files);
                    this._changeDetectorRef.detectChanges();
                }
            })
        );

        this._unsubscribableArray.push(
            // uploadFormControl (UI) -> formControl of json form (model)
            this.uploadFormControl.valueChanges.pipe(
                distinctUntilChanged(FileUploadUtils.isEqualFiles),
                tap(files => {
                    _.each(files, file => {
                        if (!file.identifier) {
                            file.uploading = true;
                        }
                    });
                    this._changeDetectorRef.detectChanges();
                }),
                switchMap((files) => this.uploadFiles(files))
            ).subscribe(files => {
                let filteredFiles = _.filter(files, file => !_.isUndefined(file));
                let formValue = _.isEmpty(filteredFiles) ? undefined : JSON.stringify(
                    _.map(filteredFiles, ({ name, size, identifier }) => ({
                        name, size, identifier,
                    }))
                );

                let fileslimit = this.options.fileslimit;
                if (fileslimit && fileslimit > 0 && files.length > fileslimit) {
                    let tempFiles = [];
                    filteredFiles.forEach(function (value, i) {
                        if (i < fileslimit) {
                            tempFiles.push(value);
                        }
                    });
                    filteredFiles = tempFiles;
                    formValue = _.isEmpty(filteredFiles) ? undefined : JSON.stringify(
                        _.map(filteredFiles, ({ name, size, identifier }) => ({
                            name, size, identifier,
                        }))
                    );
                }

                if (filteredFiles.length !== this.uploadFormControl.value.length) {
                    this.uploadFormControl.setValue(filteredFiles);
                }
                this._changeDetectorRef.detectChanges();
            })
        );
    }
}
